import 'bootstrap/dist/css/bootstrap.min.css'
import './calendar.css'

import FullCalendar, {EventClickArg, EventContentArg, EventSourceInput} from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import timeGridPlugin from '@fullcalendar/timegrid'
import moment from 'moment'
import React from 'react'
import queryString from 'query-string'
import {WithTranslation, withTranslation} from 'react-i18next'
import {RouteComponentProps, withRouter} from 'react-router-dom'
import styled from 'styled-components'

import {
  getAllActivities,
  getCurrentUser,
  getGroupsByPrograms,
} from '../../common/requests'
import {routes} from '../../common/routes'
import User from '../../dtos/User'
import {ActivityModal} from './components/ActivityModal'
import {FilterModes, IActivity, ICalendarGroup, IFilter, IFilterMode} from './types'
import uniqBy from 'lodash/uniqBy'
import range from 'lodash/range'
import fromPairs from 'lodash/fromPairs'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import FilterButtons from "../filterButtons/FilterButtons";

// NOTE: it is crucial to have the initial date to be Monday
const INITIAL_DATE = moment().day(1)

const CalendarContainer = styled.div`
  padding: 40px;
`

const Divider = styled.div`
  margin-top: 10px;
  margin-bottom: 10px;
  height: 1px;
  background-color: #eee;
`

const ROW_TITLE_WIDTH = 41
const MIN_DAY_WIDTH = 400;

const CalendarWrapper = styled.div<{ amountOfDays: number }>`
  @media only screen and (max-width: 767px) {
    min-width: ${(props: { amountOfDays: number; }) => `${ROW_TITLE_WIDTH + MIN_DAY_WIDTH * props.amountOfDays}px`}
  }
`

interface ICalendarProps extends RouteComponentProps, WithTranslation {
    programIds: number[],
    organizationId: number,
    organizationSlug: string | undefined,
    shopUrl: string | undefined,
    filterByCategoryIds: number[],
    filterByCategoryMode: IFilterMode,
}

interface ICalendarState {
    weekendsVisible: boolean,
    shouldShowEvent: boolean,
    selectedActivityId: string | null,
    selectedGroupId: string | null,
    activities: IActivity[] | undefined,
    groups: ICalendarGroup[] | undefined,
    isLoading: boolean,
    isLoggedIn: boolean,
    user: Partial<User> | null,
    hiddenDays: number[],
    startTime: string,
    endTime: string,
    lang: string,
    categories: IFilter[],
    selectedCategories: IFilter[],
    filteredCategories: IFilter[],
    filteredActivities: IActivity[],
    filteredGroups: ICalendarGroup[],
}

function renderEventContent(eventContent: EventContentArg) {
    return (
        <OverlayTrigger
            key='top'
            placement='top'
            trigger={['hover', 'focus']}
            overlay={
                <Tooltip id={`tooltip-top`}>
                    {eventContent.event.title}
                    <Divider/>
                    <b>{eventContent.timeText}</b>
                </Tooltip>
            }
        >
            <div>
                {/* <b>{eventContent.timeText}</b>
        <br /> */}
                <i>{eventContent.event.title}</i>
            </div>
        </OverlayTrigger>
    )
}

export class Calendar extends React.Component<ICalendarProps, ICalendarState> {
    state: ICalendarState = {
        weekendsVisible: true,
        activities: undefined,
        groups: undefined,
        shouldShowEvent: false,
        selectedActivityId: null,
        selectedGroupId: null,
        user: null,
        isLoading: true,
        isLoggedIn: false,
        hiddenDays: [],
        startTime: '06:00:00',
        endTime: '23:00:00',
        lang: 'fr-FR',
        categories: [],
        selectedCategories: [],
        filteredActivities: [],
        filteredGroups: [],
        filteredCategories: [],
    }

    async componentDidMount() {
        const urlParams = queryString.parse(this.props.location.search) as Record<string, string | string[] | undefined>;
        const user = await getCurrentUser() || null

        this.setState({
            user,
            isLoggedIn: Boolean(user),
            isLoading: false,
            hiddenDays: urlParams.hiddenDays ? (urlParams.hiddenDays as string).split(',').map(x => Number(x)) : [],
            startTime: urlParams.startTime
                ? String(urlParams.startTime)
                : this.state.startTime,
            endTime: urlParams.endTime
                ? String(urlParams.endTime)
                : this.state.endTime,
            lang: urlParams.language && String(urlParams.language) === 'en'
                ? 'en-EN'
                : this.state.lang,
        })

        return Promise.all([
            getGroupsByPrograms({
                organizationId: this.props.organizationId,
                programIds: this.props.programIds
            }),
            getAllActivities({organizationId: this.props.organizationId})
        ])
            .then(([{groups}, {activities}]) => {
                const [activityId, groupId] = this.props.location.hash.replace('#', '').split('-')
                if (this.props.filterByCategoryIds.length > 0) {
                    activities = this.filterActivitiesWithOneOfCategoryIds(activities, this.props.filterByCategoryIds)
                }

                groups = groups.filter(group =>
                    activities.some(activity => group.activity.id === Number(activity.id))
                );

                const categories = this.getCategoriesFromActivities(activities, groups, this.props.filterByCategoryIds, false);

                if (groups.find(x => String(x.id) === groupId && String(x.activity.id) === activityId)) {
                    this.setState({
                        shouldShowEvent: true,
                        selectedActivityId: activityId,
                        selectedGroupId: groupId,
                        groups,
                        activities,
                        categories: categories,
                        filteredActivities: activities,
                        filteredGroups: groups,
                        filteredCategories: categories,
                    })
                } else {
                    this.setState({
                        groups,
                        activities,
                        categories: categories,
                        filteredActivities: activities,
                        filteredGroups: groups,
                        filteredCategories: categories,
                    })
                }
            })
            .catch(e => {
                console.error(e)
            })
    }

    filterActivitiesByCategoryIds = (activities : IActivity[], categoryIds : number[]) : IActivity[] => {
        return activities.filter(activity =>
            activity.categories.filter((category) => {
                return categoryIds.find((categoryId) => {
                    return categoryId === Number(category.id);
                })
            }).length === categoryIds.length
        );
    }

    filterActivitiesWithOneOfCategoryIds = (activities : IActivity[], categoryIds : number[]) : IActivity[] => {
        return activities.filter(activity => {
            const category = activity.categories.find(category => {
                return  categoryIds.includes(Number(category.id));
            });

            return this.props.filterByCategoryMode.mode === FilterModes.in
                ? category
                : category === undefined;
        });
    }

  getCategoriesFromActivities(
        activities: IActivity[],
        groups: ICalendarGroup[],
        categoryIdsFilter: number[] = [],
        isFromCategorySelect: boolean
  ): IFilter[] {
        let categories: IFilter[];
        const categoryMap = new Map<number, IFilter>();
        const filterActive = categoryIdsFilter.length > 0;
        const filterModeToApply = isFromCategorySelect
          ? FilterModes.in
          : this.props.filterByCategoryMode.mode

        activities.forEach(activity => {
            const areActivityGroupsVisible = groups.some(group =>
                group.activity.id === Number(activity.id) && group.schedules.length > 0
            );

            if (areActivityGroupsVisible) {
                activity.categories.forEach(category => {
                    const categoryId = Number(category.id);
                    const notAdded = !categoryMap.has(categoryId);
                    let shouldAdd = filterModeToApply === FilterModes.in
                        ? categoryIdsFilter.includes(categoryId)
                        : !categoryIdsFilter.includes(categoryId)

                    if (notAdded && (!filterActive || shouldAdd)) {
                        categoryMap.set(categoryId, category);
                    }
                });
            }
        });

        categories = Array.from(categoryMap.values());
        categories.sort((a, b) => a.name.localeCompare(b.name));

        return categories;
    }

    goToLogin = () => {
        const redirectUrl = encodeURIComponent(`${this.props.location.pathname}${this.props.location.search}${this.props.location.hash}`)
        this.props.history.push(`${routes.authentication}/${routes.authenticationSubRoutes.login}?redirect_url=${redirectUrl}`)
    }

    handleModalOpen = () => {
        this.setState({
            shouldShowEvent: true
        })
    }

    handleModalClose = () => {
        this.props.history.push(`${this.props.location.pathname}${this.props.location.search}`)
        this.setState({
            shouldShowEvent: false,
            selectedActivityId: null,
            selectedGroupId: null,
        });
    }

    handleEventClick = (clickInfo: EventClickArg) => {
        this.handleModalOpen()
        this.props.history.push(`${this.props.location.pathname}${this.props.location.search}#${clickInfo.event.id}`)
        const [activityId, groupId] = clickInfo.event.id.split('-')
        this.setState({
            selectedActivityId: activityId,
            selectedGroupId: groupId,
        })
    }

    getSelectedGroup = () => {
        return this.state.filteredGroups && this.state.filteredGroups
            .find(x => String(x.id) === String(this.state.selectedGroupId))
    }

    onSelectActivityCategory = (newSelectedCategories: IFilter[]) => {
        const {activities = [], groups = [], categories} = this.state;

        if (newSelectedCategories.length === 0) {
            this.setState({
                selectedCategories: newSelectedCategories,
                filteredActivities: activities,
                filteredGroups: groups,
                filteredCategories: categories,
            });
            return;
        }

        let activitiesToKeep = this.filterActivitiesByCategoryIds(
            activities,
            newSelectedCategories.map((category) => Number(category.id))
        )

        const filteredActivities = activities.filter((activity) => {
            return activitiesToKeep.find((activityToKeep) => activity.id === activityToKeep.id)
        })

        const filteredGroups = groups.filter(group =>
            filteredActivities.some(activity => group.activity.id === Number(activity.id))
        );

        const categoryIds = this.state.categories.map((category) => Number(category.id))
        const filteredCategories = this.getCategoriesFromActivities(filteredActivities, filteredGroups, categoryIds, true);

        this.setState({
            selectedCategories: newSelectedCategories,
            filteredActivities: filteredActivities,
            filteredGroups: filteredGroups,
            filteredCategories: filteredCategories,
        })
    }

    render() {
        const selectedGroup = this.getSelectedGroup()
        const activityMap = new Map<String, IActivity>();
        this.state.filteredActivities.map((activity: IActivity) => activityMap.set(String(activity.id), activity))
        const selectedActivity = activityMap.get(String(this.state.selectedActivityId));
        const visibleWeek = fromPairs(
            range(7)
                .map((i) =>
                    [
                        moment(INITIAL_DATE).add(i, 'day').locale('en').format('dddd'),
                        moment(INITIAL_DATE).add(i, 'day')
                    ]
                )
        )

        return (
            <CalendarContainer className={"agenda-view container"}>
                <FilterButtons
                    onSelectionChange={this.onSelectActivityCategory}
                    filters={this.state.filteredCategories}
                    selectedFilters={this.state.selectedCategories}
                />
                {
                    selectedActivity && selectedGroup &&
                    <ActivityModal
                        user={this.state.user}
                        isOpened={this.state.shouldShowEvent}
                        onClose={this.handleModalClose}
                        activity={selectedActivity}
                        group={selectedGroup}
                        shopUrl={this.props.shopUrl}
                        goToLogin={this.goToLogin}
                        organizationSlug={this.props.organizationSlug}
                    />
                }
                {
                    (!this.state.filteredActivities || !this.state.filteredGroups) &&
                    <div>{this.props.t('offers.loading')}</div>
                }
                {
                    this.state.filteredActivities && this.state.filteredGroups &&
                    <CalendarWrapper amountOfDays={7 - this.state.hiddenDays.length}>
                        <FullCalendar
                            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                            headerToolbar={{
                                left: '',
                                center: '',
                                right: ''
                            }}
                            firstDay={1}
                            hiddenDays={this.state.hiddenDays}
                            slotMinTime={this.state.startTime}
                            slotMaxTime={this.state.endTime}
                            initialDate={INITIAL_DATE.toISOString()}
                            initialView='timeGridWeek'
                            allDaySlot={false}
                            dayHeaderFormat={{weekday: 'long'}}
                            locale={this.state.lang}
                            height="auto"
                            events={
                                uniqBy(
                                    this.state.filteredGroups
                                        .map(({id, start_date, end_date, activity, schedules, name, programmation_id}) => {
                                            let currentDate = moment(start_date)
                                            const endDate = moment(end_date)
                                            const events: EventSourceInput = []
                                            const activityDetails = activityMap.get(String(activity.id));
                                            const styleClasses = (
                                                activityDetails && [
                                                    `group-${id}`,
                                                    ...activityDetails.categories.map(({id}) => `category-${id}`),
                                                    `program-${programmation_id}`,
                                                    `activity-${activityDetails.id}`
                                                ]) || [];

                                            while (currentDate.isBefore(endDate)) {
                                                (schedules || []).forEach(x => {
                                                    if (x.type_day !== currentDate.locale('en').format('dddd')) {
                                                        return
                                                    }

                                                    events.push({
                                                        id: String(`${activity.id}-${id}`),
                                                        title: name,
                                                        start: visibleWeek[x.type_day]
                                                            .hour(moment(x.start_time).hour())
                                                            .minutes(moment(x.start_time).minutes())
                                                            .seconds(0)
                                                            .toISOString(),
                                                        end: visibleWeek[x.type_day]
                                                            .hour(moment(x.end_time).hour())
                                                            .minutes(moment(x.end_time).minutes())
                                                            .seconds(0)
                                                            .toISOString(),
                                                        classNames: [
                                                            'agenda-item',
                                                            ...styleClasses,
                                                        ]
                                                    })
                                                })
                                                currentDate = currentDate.add(1, 'day')
                                            }
                                            return events
                                        })
                                        .reduce((acc, events) => {
                                            return [...acc, ...events]
                                        }, []),
                                    x => x.id + '-' + x.start + '-' + x.end + '-' + x.title
                                )
                            }
                            eventContent={renderEventContent}
                            eventClick={this.handleEventClick}
                        />
                    </CalendarWrapper>
                }
            </CalendarContainer>
        )
    }
}

export default withRouter(withTranslation()(Calendar));
